import { useState, useEffect, useCallback } from "react";
import { getCurrentUserAsync, signIn, signOut, signInWithCredentials } from "@/services/entraIdService";
import type { EntraIdUser } from "@/services/entraIdService";

export function useAuth() {
  const [user, setUser] = useState<EntraIdUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    async function loadUser() {
      try {
        const current = await getCurrentUserAsync();
        if (active) {
          setUser(current);
          setLoading(false);
        }
      } catch (err) {
        if (active) {
          setError(err instanceof Error ? err.message : "Error al cargar usuario");
          setLoading(false);
        }
      }
    }
    loadUser();
    return () => {
      active = false;
    };
  }, []);

  const login = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const u = await signIn();
      setUser(u);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  }, []);

  const loginWithCredentials = useCallback(async (username: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      const u = await signInWithCredentials(username, password);
      setUser(u);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Error al iniciar sesión";
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    setLoading(true);
    try {
      await signOut();
      setUser(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al cerrar sesión");
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    user,
    loading,
    error,
    login,
    loginWithCredentials,
    logout,
    isAuthenticated: user !== null,
  };
}